import { useEffect, useRef, useState } from "react"

import Popover from "@mui/material/Popover"
import cn from "classnames"

import DefaultModal from "@ui/DefaultModal"
import { getAttendanceItems } from "@utils/peopleUtils"
import { useUserStatusChange } from "hooks"

import ModalHeader from "../../../ui/DefaultModal/Header"
import ActionButton from "../../ActionButton"
import StatusIcon from "../../Content/Participants/ParticipantItem/StatusIcon"

import Options from "./Options"

import "./index.scss"

// `labelOverride`: People Card Collapsed spec (5ba3f8cbdf4c43f4a20d3bc91beea892) calls for a
// static "Change Status" action button in the collapsed action row (TeeupPeople/SubTitleContent),
// distinct from this component's existing usage in TitleContent's row where the button always
// shows the user's live status (StatusIcon + "Going"/"RSVP"/etc). Passing labelOverride renders
// that fixed text instead of the dynamic status label; omitted (default), behavior is unchanged.
//
// `anchored`: People Card Detail-Groups spec (d45923fee2264c559a5512601920958c, QJ defect 4 --
// "Change Status expands in the wrong place"). This modal's default presentation
// (`@ui/DefaultModal`) is a viewport-centered MUI `Modal` (position: fixed, 50%/50%, see that
// component) -- fine for every other usage, but the ratified My Status detail group
// (Content/YourStatus) specifically requires the dialog to open IN PLACE, anchored to that
// group, not floating in the middle of the screen. Passing `anchored` swaps the presentation to
// an MUI `Popover` anchored to this component's own button (via `anchorRef`) instead of
// `DefaultModal` -- same trigger button, same Options content, same status-change handler; only
// every OTHER existing usage (TitleContent's collapsed-row pill, SubTitleContent's "Change
// Status" button) is completely unaffected since they omit the prop (default false).
const AttendanceModal = ({ user, labelOverride, anchored }) => {
  const [open, setOpen] = useState(false)
  const [whereAreYouOptions, setWhereAreYouOptions] = useState([])
  const [areYouGoingOptions, setAreYouGoingOptions] = useState([])
  const anchorRef = useRef(null)

  const { handleUserStatusChange, statusExtended } = useUserStatusChange(user)

  useEffect(() => {
    const wherePick = new Set(["runningLate", "arrived", "onmyway"])
    const goingPick = new Set(["going", "mightgo", "notgoing"])

    const attendance = getAttendanceItems()
    const whereAreYouOptions = attendance.filter(item =>
      wherePick.has(item.status)
    )
    const areYouGoingOptions = attendance.filter(item =>
      goingPick.has(item.status)
    )

    setAreYouGoingOptions(areYouGoingOptions)
    setWhereAreYouOptions(whereAreYouOptions)
  }, [])

  const handleStatusClick = item => {
    handleUserStatusChange(item.status)
    setOpen(false)
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const dialogContent = (
    <div className="people-attendance-content">
      <ModalHeader
        title="My Attendance"
        subtitle="Let others know your status."
        onClose={handleClose}
      />

      <div className="people-attendance-options-container">
        <Options
          title="Are you going?"
          options={areYouGoingOptions}
          onClick={handleStatusClick}
        />
        <Options
          title="Where are you?"
          options={whereAreYouOptions}
          onClick={handleStatusClick}
        />
      </div>
    </div>
  )

  return (
    <div ref={anchorRef}>
      <ActionButton
        onClick={handleOpen}
        // BUG FIX (found live via headless verification at 1440px viewport, below the 1500px
        // breakpoint): `.people-attendance-button`'s narrow-viewport rule (index.scss) hides
        // `.people-attendance-button-label` and falls back to the StatusIcon -- fine for the
        // dynamic-status usage in TitleContent (always has an icon), but the labelOverride
        // button has NO icon, so at <=1500px it rendered as a totally blank pill. Give the
        // labelOverride button its own class instead so it's exempt from that compression.
        className={cn({
          "people-attendance-button": !labelOverride,
          "people-attendance-button-static": labelOverride,
        })}
        variant="primary">
        {labelOverride && (
          <span className="people-attendance-button-label">
            {labelOverride}
          </span>
        )}

        {!labelOverride && statusExtended && (
          <>
            <StatusIcon status={statusExtended} isBlock isLarge />
            {/* FIX 2 (2026-07-04, People row truncation at 1280px): label wrapped in its own
                span so the narrow-viewport rule below can compress this fixed-width control to
                icon-only, freeing width for Organizer + avatar stack + "N people" on the left
                (Ale's layout -- those must never truncate). */}
            <span className="people-attendance-button-label">
              {statusExtended.text}
            </span>
          </>
        )}

        {!labelOverride && !statusExtended && (
          <span className="people-attendance-button-label">RSVP</span>
        )}
      </ActionButton>

      {anchored ? (
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          className="people-attendance-popover"
          slotProps={{ paper: { className: "people-attendance-popover-paper" } }}>
          {dialogContent}
        </Popover>
      ) : (
        <DefaultModal open={open} onClose={handleClose}>
          {dialogContent}
        </DefaultModal>
      )}
    </div>
  )
}

export default AttendanceModal
